import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories } from '@/mocks/products';

export default function ProductsShowcase() {
  const { t } = useTranslation();
  const [activeCat, setActiveCat] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const category = productCategories[activeCat];
  const products = category.products;

  const scrollTo = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-14 md:py-20 bg-warm-50" id="products">
      <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-brand-600 mb-2">
            {t('products.eyebrow', 'Explore Products')}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-warm-900 mb-3">
            {t('products.title', 'Featured Products')}
          </h2>
          <p className="text-warm-600 max-w-2xl mx-auto text-sm md:text-base">
            {t('products.description', 'Browse curated products from the CSSBuy Spreadsheet. Click any item to view details and purchase on the main site.')}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 md:gap-3 overflow-x-auto pb-4 mb-8 scrollbar-hide justify-start md:justify-center">
          {productCategories.map((cat, idx) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCat(idx)}
              className={`whitespace-nowrap px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer ${
                idx === activeCat
                  ? 'bg-brand-600 text-white'
                  : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Category Link */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg md:text-xl font-bold text-warm-900">
            {category.name}
          </h3>
          <a
            href={category.link}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm text-brand-600 hover:text-brand-700 font-medium transition-colors flex items-center gap-1 cursor-pointer"
          >
            {t('products.viewAll', 'View All')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Products Grid with Navigation */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scrollTo('left')}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/90 border border-warm-200 text-warm-600 hover:bg-brand-600 hover:text-white hover:border-brand-600 transition-all cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          {/* Products Scroll Container */}
          <div
            ref={scrollRef}
            className="flex gap-4 md:gap-5 overflow-x-auto pb-4 scrollbar-hide scroll-smooth snap-x snap-mandatory"
          >
            {products.length > 0 ? (
              products.map((product) => (
                <a
                  key={product.id}
                  href={product.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex-shrink-0 w-[220px] md:w-[260px] snap-start cursor-pointer"
                >
                  <div className="bg-white rounded-xl border border-warm-200 overflow-hidden hover:border-brand-300 transition-all duration-300 hover:-translate-y-1">
                    {/* Product Image */}
                    <div className="relative w-full h-[220px] md:h-[280px] overflow-hidden bg-warm-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Product%20placeholder%20minimal%20white%20background%20elegant%20square&width=300&height=300&seq=product-fallback&orientation=squarish';
                        }}
                      />
                    </div>

                    {/* Product Info */}
                    <div className="p-3 md:p-4">
                      <h4 className="text-xs md:text-sm font-semibold text-warm-900 line-clamp-2 mb-2 leading-snug min-h-[2.5rem]">
                        {product.name}
                      </h4>
                      <div className="flex items-center justify-between">
                        <span className="text-sm md:text-base font-bold text-brand-700">
                          {product.price}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 bg-brand-50 px-2.5 py-1 rounded-full group-hover:bg-brand-600 group-hover:text-white transition-all">
                          <i className="ri-shopping-bag-line" />
                          {t('products.buy', 'Buy')}
                        </span>
                      </div>
                    </div>
                  </div>
                </a>
              ))
            ) : (
              <div className="w-full text-center py-12 text-warm-500">
                {t('products.noProducts', 'Products coming soon')}
              </div>
            )}
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => scrollTo('right')}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 items-center justify-center rounded-full bg-white/90 border border-warm-200 text-warm-600 hover:bg-brand-600 hover:text-white hover:border-brand-600 transition-all cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}